/**
 * BaseLayout - Reusable layout component with top navbar and left sidebar
 * This is the foundation layout that all modules should use for consistency
 * Uses shadcn/ui components for professional design system compliance
 */

import React, { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Search, Bell, Menu, X, LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useOffline } from '../../contexts/OfflineContext';
import { profileService } from '@/services/admin/profile.service';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

// Helper function to get the correct profile route based on user appType and roles
const getProfileRoute = (appType?: string, roles?: string[]): string => {
  // Check if user has SALES_TEAM role specifically
  if (roles && roles.some(role => role === 'SALES_TEAM' || role === 'CBG_SALES' || role === 'SALES_EXECUTIVE')) {
    return '/sales/profile';
  }

  // Otherwise, use appType-based routing
  switch (appType) {
    case 'admin':
      return '/admin/profile';
    case 'biogassangh':
      return '/cluster/profile';
    case 'gausakhi':
      return '/gaushala/profile';
    case 'urjavyapar':
      return '/commerce/profile'; // Commerce users (non-sales)
    case 'urjasanyojak':
      return '/reporting/profile';
    case 'shuddhidoot':
      return '/purification/profile';
    case 'sales':
      return '/sales/profile';
    case 'transport':
    case 'transporter':
    case 'urjavaahak':
      return '/transport/profile';
    default:
      return '/gaushala/profile'; // Default fallback
  }
};

/**
 * Dynamic System Status Component
 * Shows real-time online/offline status in the sidebar footer
 */
const DynamicSystemStatus: React.FC = () => {
  const { isOnline, pendingSyncCount, isSyncing, triggerSync } = useOffline();

  return (
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center text-sm">
          <div
            className={cn(
              'w-2 h-2 rounded-full mr-2 transition-colors',
              isOnline ? 'bg-green-500' : 'bg-red-500'
            )}
          />
          <span className={isOnline ? 'text-gray-600' : 'text-red-600 font-medium'}>
            {isOnline ? 'System Online' : 'System Offline'}
          </span>
        </div>
        {pendingSyncCount > 0 && (
          <button
            onClick={() => isOnline && triggerSync()}
            disabled={!isOnline || isSyncing}
            className={cn(
              'text-xs px-2 py-1 rounded-full',
              isOnline
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
            )}
            title={isOnline ? 'Click to sync' : 'Waiting for connection'}
          >
            {isSyncing ? 'Syncing...' : `${pendingSyncCount} pending`}
          </button>
        )}
      </div>
    </div>
  );
};

export interface NavigationItem {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  isActive?: boolean;
  badge?: string | number;
}

export interface BreadcrumbItem {
  label: string;
  url?: string;
  onClick?: () => void;
}

export interface BaseLayoutProps {
  /** Module name displayed in the navbar (e.g., "Gausakhi") */
  moduleName: string;
  /** Navigation items for the left sidebar */
  navigationItems: NavigationItem[];
  /** Main content to render */
  children: ReactNode;
  /** Optional module subtitle */
  moduleSubtitle?: string;
  /** Optional user info in top right */
  userInfo?: {
    name: string;
    role: string;
    avatar?: string;
  };
  /** Breadcrumb items to show navigation path */
  breadcrumbs?: BreadcrumbItem[];
  /** Custom className for the main content area */
  contentClassName?: string;
  /** Optional callback when user profile is clicked */
  onProfileClick?: () => void;
  /** Hide the page header section */
  hideHeader?: boolean;
}

export default function BaseLayout({
  moduleName,
  navigationItems,
  children,
  moduleSubtitle,
  userInfo,
  breadcrumbs,
  contentClassName,
  onProfileClick,
  hideHeader = false
}: BaseLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { logout, user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  // Handler for profile click - executes navigation only if route is not null
  const handleProfileClick = () => {
    console.log('✅ Profile click handler invoked!');
    console.log('👤 User data:', { appType: user?.appType, roles: user?.roles });
    if (onProfileClick) {
      console.log('🔄 Calling custom onProfileClick callback');
      try {
        onProfileClick();
      } catch (error) {
        console.error('❌ Error in onProfileClick:', error);
        const profileRoute = getProfileRoute(user?.appType, user?.roles);
        console.log('🔀 Navigating to profile route:', profileRoute);
        navigate(profileRoute);
      }
    } else {
      console.log('📍 No custom callback, using default navigation');
      const profileRoute = getProfileRoute(user?.appType, user?.roles);
      console.log('🔀 Navigating to profile route:', profileRoute);
      navigate(profileRoute);
    }
  };

  // Debug logging
  console.log('BaseLayout received userInfo:', userInfo);
  console.log('BaseLayout received onProfileClick:', typeof onProfileClick);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);

    // Call ProfileService logout (clears server session + local storage)
    await profileService.logout();

    // Also call AuthContext logout to clear local state
    await logout();

    // Show success message
    toast.success('Logged out successfully');

    setIsLoggingOut(false);
    setShowLogoutDialog(false);

    // Navigate to login page
    navigate('/login');
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  const handleLanguageToggle = () => {
    const newLanguage = language === 'en' ? 'hi' : 'en';
    setLanguage(newLanguage); // Context handles localStorage
    toast.success(newLanguage === 'hi' ? 'भाषा हिंदी में बदल गई' : 'Language changed to English');
  };

  const handleSearchClick = () => {
    setShowSearchDialog(true);
    setSearchQuery('');
  };

  const handleSearchClose = () => {
    setShowSearchDialog(false);
    setSearchQuery('');
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      toast.info(`Searching for: ${searchQuery}`);
      // TODO: Implement actual search functionality
      logger.info('Search query:', searchQuery);
      handleSearchClose();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Left Sidebar - Fixed position */}
      <aside className={`fixed left-0 top-0 w-64 h-screen bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Application Branding - SAUBHAGYA */}
          <div className="h-auto bg-white border-b border-gray-200 flex items-center px-4 py-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base">S</span>
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-bold text-gray-900">SAUBHAGYA</h2>
                <p className="text-xs text-blue-600 font-medium">Unified Platform</p>
              </div>
            </div>
          </div>

          {/* Module Title */}
          <div className="bg-gray-50 border-b border-gray-100 flex items-center px-4 py-3">
            <div className="flex items-center w-full">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden mr-2 p-1 -ml-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">{moduleName}</h3>
                {moduleSubtitle && (
                  <p className="text-xs text-gray-500 mt-0.5">{moduleSubtitle}</p>
                )}
              </div>
            </div>
          </div>

          {/* Mobile close button */}
          <div className="lg:hidden flex justify-between items-center px-4 py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-900">Navigation</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-1"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => {
                    item.onClick();
                    setIsMobileMenuOpen(false);
                  }}
                  variant={item.isActive ? 'default' : 'ghost'}
                  className={`w-full justify-start h-10 font-medium text-sm transition-all duration-200 ${
                    item.isActive
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className={`mr-3 flex-shrink-0 ${item.isActive ? 'text-white' : 'text-gray-500'}`}>
                    {item.icon}
                  </span>
                  <span className="flex-1 text-left truncate" title={item.label}>{item.label}</span>
                  {item.badge && (
                    <Badge
                      variant={item.isActive ? "outline" : "secondary"}
                      className={`ml-auto text-xs ${
                        item.isActive
                          ? 'border-white/30 text-white bg-white/10'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Button>
              ))}
            </div>
          </nav>

          {/* Footer - Dynamic Online/Offline Status */}
          <DynamicSystemStatus />
        </div>
      </aside>

      {/* Main Content Area - Offset by sidebar width */}
      <div className="lg:ml-64 min-h-screen">
        {/* Top Navbar - Fixed */}
        <header className="fixed top-0 left-0 right-0 lg:left-64 h-16 bg-white border-b border-gray-200 z-40 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden p-1 -ml-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Current Module/Page Name */}
            <h1 className="text-lg font-semibold text-gray-900 truncate">{moduleName}</h1>
          </div>

          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSearchClick}
              title="Search"
              className="hover:bg-gray-100"
            >
              <Search className="w-4 h-4" />
              <span className="sr-only">Search</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="relative hover:bg-gray-100"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-2 w-2 p-0 text-[10px] flex items-center justify-center"
              >
                2
              </Badge>
              <span className="sr-only">Notifications</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLanguageToggle}
              title={language === 'en' ? 'Switch to Hindi' : 'Switch to English'}
              className={`hover:bg-gray-100 ${language === 'hi' ? 'bg-blue-50 text-blue-600' : ''}`}
            >
              <span className="text-sm font-medium">{language === 'en' ? 'हिं' : 'EN'}</span>
            </Button>

            <Separator orientation="vertical" className="h-6 mx-1" />

            {userInfo && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    console.log('✅ Text button clicked!');
                    console.log('👤 User data:', { appType: user?.appType, roles: user?.roles });
                    if (onProfileClick) {
                      console.log('Calling onProfileClick...');
                      onProfileClick();
                    } else {
                      console.log('No onProfileClick provided, navigating to profile route');
                      const profileRoute = getProfileRoute(user?.appType, user?.roles);
                      console.log('🔀 Navigating to:', profileRoute);
                      navigate(profileRoute);
                    }
                  }}
                  type="button"
                  className="text-right text-xs hidden sm:block hover:bg-gray-100 rounded px-2 py-1 transition-colors cursor-pointer"
                  title="Click to view profile"
                >
                  <div className="font-medium text-gray-900 truncate">{userInfo.name}</div>
                  <div className="text-xs text-gray-500 truncate">{userInfo.role}</div>
                </button>
                <button
                  onClick={() => {
                    console.log('✅ Avatar button clicked!');
                    console.log('👤 User data:', { appType: user?.appType, roles: user?.roles });
                    if (onProfileClick) {
                      console.log('Calling onProfileClick...');
                      onProfileClick();
                    } else {
                      console.log('No onProfileClick provided, navigating to profile route');
                      const profileRoute = getProfileRoute(user?.appType, user?.roles);
                      console.log('🔀 Navigating to:', profileRoute);
                      navigate(profileRoute);
                    }
                  }}
                  type="button"
                  className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors cursor-pointer flex-shrink-0"
                  title="Click to view profile"
                >
                  {userInfo.avatar ? (
                    <img src={userInfo.avatar} alt={userInfo.name} className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <span className="text-white text-xs font-medium">
                      {userInfo.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogoutClick}
                  className="text-gray-600 hover:text-red-600 hover:bg-red-50 -mr-2"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="sr-only">Logout</span>
                </Button>
              </div>
            )}
          </div>
        </header>

        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="fixed top-16 left-0 right-0 lg:left-64 bg-gray-50 border-b border-gray-200 z-30 flex items-center px-4 lg:px-6 py-2">
            <nav className="flex items-center space-x-1 text-xs lg:text-sm text-gray-600 overflow-x-auto">
              {breadcrumbs.map((item, index) => (
                <div key={index} className="flex items-center flex-shrink-0">
                  {index > 0 && <span className="mx-1.5 text-gray-400">/</span>}
                  {item.onClick || item.url ? (
                    <button
                      onClick={item.onClick}
                      className="hover:text-blue-600 transition-colors font-medium hover:underline whitespace-nowrap"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className={`whitespace-nowrap ${index === breadcrumbs.length - 1 ? "text-gray-900 font-medium" : ""}`}>
                      {item.label}
                    </span>
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}

        {/* Main Content */}
        <main
          className={cn(
            "min-h-screen w-full p-6",
            breadcrumbs && breadcrumbs.length > 0 ? "pt-28" : "pt-20",
            contentClassName
          )}
        >
          {children}
        </main>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to logout? You will need to login again to access your account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleLogoutCancel}
              disabled={isLoggingOut}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLogoutConfirm}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging out...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Search System</DialogTitle>
            <DialogDescription>
              Search across modules, users, records, and system settings
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Type to search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              className="w-full"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleSearchClose}>
              Cancel
            </Button>
            <Button onClick={handleSearchSubmit} disabled={!searchQuery.trim()}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}